/**
 * 表单校验工具函数
 */

export interface CompanionValidationResult {
  isValid: boolean;
  duplicates: string[];
  message?: string;
}

export interface Companion {
  employeeId: string;
  employeeName: string;
}

/**
 * 校验同行人是否重复（包括与申请人重复）
 *
 * 规则1：同行人中不能包含申请人本人
 * 规则2：同行人列表内部不能有重复人员
 *
 * @param applicantId 申请人ID
 * @param companions 同行人列表
 * @returns 校验结果
 */
export function validateCompanions(
  applicantId: string,
  companions: Companion[]
): CompanionValidationResult {
  if (!companions || companions.length === 0) {
    return { isValid: true, duplicates: [] };
  }

  const allIds = [applicantId, ...companions.map(c => c.employeeId)];

  // 查找重复
  const duplicates = allIds.filter(
    (id, index) => allIds.indexOf(id) !== index
  );

  if (duplicates.length > 0) {
    // 找出重复人员的名字
    const duplicateNames = companions
      .filter(c => duplicates.includes(c.employeeId))
      .map(c => c.employeeName);

    if (duplicates.includes(applicantId)) {
      return {
        isValid: false,
        duplicates: [...new Set(duplicates)],
        message: '人员重复：同行人中不能包含申请人本人'
      };
    }

    return {
      isValid: false,
      duplicates: [...new Set(duplicates)],
      message: `人员重复：同行人名单中存在重复人员（${duplicateNames.join('、')}）`
    };
  }

  return { isValid: true, duplicates: [] };
}

/**
 * 校验跨城单段目的城市列表
 *
 * 规则：
 * 1. 至少 1 个目的城市
 * 2. 最多 50 个目的城市
 * 3. 不能与出发城市相同
 * 4. 城市名不能重复
 */
export interface DestinationCityValidationResult {
  isValid: boolean
  message?: string
}

export function validateDestinationCities(
  cities: Array<{ name: string; code: string }> | undefined,
  departureCity: string | undefined
): DestinationCityValidationResult {
  if (!cities || cities.length === 0) {
    return { isValid: false, message: '至少需要一个目的城市' }
  }
  if (cities.length > 50) {
    return { isValid: false, message: '最多添加 50 个目的城市' }
  }
  const seen = new Set<string>()
  for (const city of cities) {
    if (!city.name || city.name.trim() === '') {
      return { isValid: false, message: '目的城市名称不能为空' }
    }
    if (departureCity && city.name === departureCity) {
      return { isValid: false, message: `目的城市不能与出发城市相同：${city.name}` }
    }
    if (seen.has(city.name)) {
      return { isValid: false, message: `目的城市不能重复：${city.name}` }
    }
    seen.add(city.name)
  }
  return { isValid: true }
}
